/**
 * The provider shell both SPAs mount. Neither app defines a theme of its own.
 *
 * THE STYLESHEET IMPORT ORDER BELOW IS NOT ARBITRARY — Mantine's documentation carries a warning
 * on it. `@mantine/core/styles.css` must come first; every other package's stylesheet builds on
 * its variables and layer order (docs/ui/02-theme.md).
 */
import { localStorageColorSchemeManager, MantineProvider } from '@mantine/core';
import type { MantineColorSchemeManager } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { theme, themeSpeaking } from './theme/theme';
import { cssVariablesResolver } from './theme/css-variables';
import { useDatesLocale } from './texts/dates-locale';
import { UiTextsProvider, englishUiText } from './texts/ui-texts';
import type { UiTranslate } from './texts/ui-texts';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import './theme/global.css';

export interface AppProvidersProps {
  children: ReactNode;
  /**
   * The language the shared components speak, one of the platform's codes (`de`): the date
   * pickers' month and day names follow it. Absent, as in the admin console: English.
   */
  language?: string;
  /** Their words in that language (./texts/ui-texts.tsx). Absent: the English defaults. */
  translate?: UiTranslate;
}

/**
 * THERE IS NO `ColorSchemeScript` HERE, AND THAT IS A DECISION.
 *
 * It is Mantine's no-flash-on-first-paint helper, and docs/ui/02-theme.md recommends it
 * while marking it optional under ADR-0007's no-SSR ruling. In a client-only Vite app it works by
 * emitting an INLINE script — and adr/0007-web-app-stack.md fixes a CSP of `default-src 'self'`
 * with no inline scripts, which blocks it outright. A blocked helper is worse than an absent one,
 * because it reads as present. Each app's index.html pins `data-mantine-color-scheme="dark"`
 * statically instead, which needs no script and matches the brand's own default; the provider
 * replaces it on mount.
 */

/** Mantine's default key, kept so a reader's stored choice survives. */
const COLOR_SCHEME_KEY = 'mantine-color-scheme-value';

/**
 * Mantine's localStorage manager, minus one loop. On a `storage` event from another tab Mantine
 * adopts the value AND writes it back. In Safari two tabs can share a web process, so the event
 * can arrive after storage has moved on; the write-back then restores the older value, the other
 * tab does the same, and both pages flip light/dark without end (teammate report, 19 October
 * 2026). So only an event that matches what storage holds now is adopted, and its write-back
 * changes nothing.
 */
function crossTabColorSchemeManager(): MantineColorSchemeManager {
  const manager = localStorageColorSchemeManager({ key: COLOR_SCHEME_KEY });
  return {
    ...manager,
    subscribe: (onUpdate) => {
      manager.subscribe((value) => {
        if (value === window.localStorage.getItem(COLOR_SCHEME_KEY)) {
          onUpdate(value);
        }
      });
    },
  };
}

export function AppProviders({ children, language, translate }: AppProvidersProps) {
  // One per provider: a manager keeps its listener in a closure, so two must not share one.
  const [colorSchemeManager] = useState(crossTabColorSchemeManager);
  const spoken = useMemo(
    () => (translate === undefined ? theme : themeSpeaking(translate)),
    [translate],
  );
  const datesLocale = useDatesLocale(language);
  return (
    <MantineProvider
      theme={spoken}
      cssVariablesResolver={cssVariablesResolver}
      colorSchemeManager={colorSchemeManager}
      /* Follow the reader's system setting. A toggle overrides it; see ./theme/color-scheme. */
      defaultColorScheme="auto"
    >
      {/*
        Exactly one Notifications, anywhere inside the provider. It is a COMPONENT, not a
        provider — nesting a second one gives two toast stacks and no error.

        Mantine's own container has NO live region: `aria-|role=|scope=` returns zero hits in its
        Notifications.tsx, and each notification renders role="alert", which is implicitly urgent.
        So a routine "Draft saved" would interrupt a screen-reader user mid-sentence. The wrapper
        that fixes this — role="status" for ordinary confirmations, role="alert" kept for failures
        — belongs to the `toast` component in the 39-component inventory, which is not M0's. This
        comment is here so that gap is visible rather than assumed closed
        (docs/ui/03-components.md:180-185).
      */}
      <Notifications position="bottom-right" limit={3} />
      <UiTextsProvider translate={translate ?? englishUiText}>
        <DatesProvider settings={{ locale: datesLocale }}>{children}</DatesProvider>
      </UiTextsProvider>
    </MantineProvider>
  );
}
